"use client"

import * as React from 'react'
import Link from 'next/link'
import { z } from 'zod'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { ErrorMessage } from '@open-mercato/ui/backend/detail'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { Button } from '@open-mercato/ui/primitives/button'
import { Label } from '@open-mercato/ui/primitives/label'
import { Textarea } from '@open-mercato/ui/primitives/textarea'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import type { TranslateFn } from '@open-mercato/shared/lib/i18n/context'
import { locationPayloadSchema, type LocationPayloadInput } from '../../../data/validators'

const bulkTemplate = JSON.stringify(
  [
    {
      name: 'DC Warsaw',
      type: 'WAREHOUSE',
      address: {
        street: 'ul. Logistyczna 12',
        city: 'Warszawa',
        postalCode: '03-236',
        country: 'PL',
      },
      latitude: 52.2904,
      longitude: 21.0233,
      supportedModes: ['ROAD'],
      capabilities: ['RAMP', 'COLD_STORAGE'],
      slotBookingEnabled: true,
      isActive: true,
    },
  ],
  null,
  2,
)

function describeBulkParseError(
  error: z.ZodError,
  t: TranslateFn,
) {
  const issue = error.issues[0]
  if (!issue) {
    return t('locations.bulk.errors.invalidSchema', 'Bulk payload is invalid.')
  }

  const itemIndex = typeof issue.path[0] === 'number' ? Number(issue.path[0]) + 1 : null
  const fieldPath = issue.path
    .slice(typeof issue.path[0] === 'number' ? 1 : 0)
    .map((segment) => String(segment))
    .join('.')

  if (itemIndex !== null && fieldPath.length > 0) {
    return t(
      'locations.bulk.errors.invalidItemField',
      'Location {{index}} has invalid field "{{field}}": {{message}}',
      { index: itemIndex, field: fieldPath, message: issue.message },
    )
  }

  if (itemIndex !== null) {
    return t(
      'locations.bulk.errors.invalidItem',
      'Location {{index}} is invalid: {{message}}',
      { index: itemIndex, message: issue.message },
    )
  }

  return t(
    'locations.bulk.errors.invalidSchemaWithMessage',
    'Bulk payload is invalid: {{message}}',
    { message: issue.message },
  )
}

function parseBulkPayload(
  rawValue: string,
  t: TranslateFn,
) {
  const trimmed = rawValue.trim()
  if (!trimmed) {
    throw new Error(t('locations.bulk.errors.empty', 'Paste a JSON array of locations first.'))
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch {
    throw new Error(t('locations.bulk.errors.invalidJson', 'Bulk payload must be valid JSON.'))
  }

  if (!Array.isArray(parsed)) {
    throw new Error(t('locations.bulk.errors.arrayRequired', 'Bulk payload must be a JSON array.'))
  }

  if (parsed.length === 0) {
    throw new Error(t('locations.bulk.errors.emptyArray', 'Bulk payload must contain at least one location.'))
  }

  try {
    return z.array(locationPayloadSchema).parse(parsed)
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new Error(describeBulkParseError(error, t))
    }
    throw error
  }
}

export default function LocationsBulkCreatePage() {
  const t = useT()
  const router = useRouter()
  const [rawValue, setRawValue] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [isSaving, setIsSaving] = React.useState(false)

  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'locations:bulk:create',
  })

  const handleSubmit = React.useCallback(async () => {
    let payloads: LocationPayloadInput[]
    try {
      payloads = parseBulkPayload(rawValue, t)
      setError(null)
    } catch (parseError) {
      const message = parseError instanceof Error
        ? parseError.message
        : t('locations.bulk.errors.invalidSchema', 'Bulk payload is invalid.')
      setError(message)
      flash(message, 'error')
      return
    }

    let createdCount = 0
    setIsSaving(true)
    try {
      await runMutation({
        operation: async () => {
          for (const payload of payloads) {
            await createCrud('freight/locations', payload, {
              errorMessage: t('locations.bulk.errors.create', 'Failed to create locations from bulk payload.'),
            })
            createdCount += 1
          }
        },
        context: { entityType: 'locations:location', count: payloads.length },
        mutationPayload: { count: payloads.length },
      })
      flash(
        t(
          'locations.bulk.messages.created',
          'Created {{count}} locations.',
          { count: createdCount },
        ),
        'success',
      )
      router.push('/backend/locations')
    } catch (submitError) {
      const baseMessage = submitError instanceof Error
        ? submitError.message
        : t('locations.bulk.errors.create', 'Failed to create locations from bulk payload.')
      const message = createdCount > 0
        ? t(
          'locations.bulk.errors.partial',
          'Created {{count}} locations before import stopped. {{message}}',
          { count: createdCount, message: baseMessage },
        )
        : baseMessage
      setError(message)
      flash(message, 'error')
    } finally {
      setIsSaving(false)
    }
  }, [rawValue, router, runMutation, t])

  return (
    <Page>
      <PageBody className="max-w-4xl space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold">
              {t('locations.bulk.title', 'Bulk add locations')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t(
                'locations.bulk.description',
                'Paste a JSON array of location payloads to create multiple records in one run.',
              )}
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link href="/backend/locations">
              {t('common.cancel', 'Cancel')}
            </Link>
          </Button>
        </div>

        {error ? <ErrorMessage label={error} /> : null}

        <div className="space-y-3 rounded-lg border bg-background p-4">
          <div className="space-y-2">
            <Label htmlFor="locations-bulk-json">
              {t('locations.bulk.fields.payload', 'Locations JSON payload')}
            </Label>
            <Textarea
              id="locations-bulk-json"
              value={rawValue}
              onChange={(event) => setRawValue(event.target.value)}
              rows={18}
              placeholder={t(
                'locations.bulk.fields.payloadPlaceholder',
                'Paste a JSON array with location payloads.',
              )}
              className="font-mono text-xs"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {t(
              'locations.bulk.help',
              'Use the same payload shape as POST /api/freight/locations, without tenantId and organizationId.',
            )}
          </p>
        </div>

        <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold">
              {t('locations.bulk.exampleTitle', 'Example payload')}
            </h2>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setRawValue(bulkTemplate)}
            >
              {t('locations.bulk.actions.useExample', 'Use example')}
            </Button>
          </div>
          <pre className="overflow-x-auto rounded-md bg-background p-3 text-xs text-muted-foreground">
            <code>{bulkTemplate}</code>
          </pre>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => { void handleSubmit() }}
            disabled={isSaving}
          >
            {isSaving
              ? t('locations.bulk.actions.creating', 'Creating...')
              : t('locations.bulk.actions.create', 'Create locations')}
          </Button>
          <Button variant="outline" asChild>
            <Link href="/backend/locations">
              {t('common.cancel', 'Cancel')}
            </Link>
          </Button>
        </div>
      </PageBody>
    </Page>
  )
}
